import {HeatProfileDtoV1, UpsertHeatProfileDto} from 'puffco-api-axios-client';
import React from 'react';
import {useSelector} from 'react-redux';

import {heatProfilesApi} from '../api/apis';
import {
  connectedPeakSelector,
  currentDeviceHeatProfilesSelector,
  currentDeviceSelector,
  updateDeviceHeatProfiles,
} from '../redux/slices/bleSlice';
import {
  allAndCurrentDeviceMoodLightsSelector,
  upsertAccountMoodLights,
  upsertExclusiveMoodLights,
} from '../redux/slices/moodLightSlice';
import {
  accountHeatProfilesSelector,
  upsertAccountHeatProfiles as reduxUpsertAccountHeatProfiles,
} from '../redux/slices/profilesSlice';
import {userSelector} from '../redux/slices/userSlice';
import {useAppDispatch} from '../redux/useAppDispatch';
import {
  HeatProfile,
  MaybeAggregatedHeatProfile,
  isColorLight,
  isMoodLightId,
} from '../types/HeatProfile';
import {MoodLight} from '../types/MoodLight';
import {toHeatCycle} from '../utils/toHeatCycle';
import {useAsyncFn} from './useAsyncFn';
import {extractMoodLights} from './useLoadAccountHeatProfiles';

type UpsertHeatProfile = HeatProfileDtoV1 | HeatProfile;

const mapToUpsertHeatProfilesBody = (heatProfiles: UpsertHeatProfile[]) => ({
  upsertHeatProfilesBody: {
    items: heatProfiles.map<UpsertHeatProfileDto>(item => ({
      id: item.id,
      name: item.name,
      temperature: item.temperature,
      units: item.units,
      duration: item.duration,
      version: item.version,
      vaporSetting: item.vaporSetting,
      order: item.order,
      lighting: item.lighting,
      created: item.created,
      modified: item.modified,
      light: isColorLight(item.light) ? item.light : {id: item.light.id},
    })),
  },
});

export const useUpsertHeatProfiles = () => {
  const user = useSelector(userSelector);
  const deviceHeatProfiles = useSelector(currentDeviceHeatProfilesSelector);
  const accountHeatProfiles = useSelector(accountHeatProfilesSelector);
  const peak = useSelector(connectedPeakSelector);
  const currentDevice = useSelector(currentDeviceSelector);
  const moodLights = useSelector(allAndCurrentDeviceMoodLightsSelector);
  const dispatch = useAppDispatch();

  const getMoodLight = React.useCallback(
    (id: string) => moodLights[id] as MoodLight | undefined,
    [moodLights],
  );

  const upsertRemoteHeatProfiles = React.useCallback(
    async (heatProfilesToBeUpdated: MaybeAggregatedHeatProfile[]) => {
      if (!user) return {accountHeatProfiles: heatProfilesToBeUpdated};

      const {items: accountHeatProfiles} = await heatProfilesApi
        .upsertHeatProfiles(
          mapToUpsertHeatProfilesBody(heatProfilesToBeUpdated),
        )
        .then(({data}) => data);

      const {exclusiveMoodLights, accountMoodLights} =
        extractMoodLights(accountHeatProfiles);

      return {accountHeatProfiles, exclusiveMoodLights, accountMoodLights};
    },
    [user],
  );

  return useAsyncFn(
    async (profiles: MaybeAggregatedHeatProfile[]) => {
      const aggregated = profiles.map(item => ({
        ...item,
        light: isMoodLightId(item.light)
          ? moodLights[item.light.id] ?? item.light
          : item.light,
      }));
      const onDevice = aggregated.filter(hp =>
        deviceHeatProfiles.some(dhp => dhp.id === hp.id),
      );

      const {
        accountHeatProfiles: ahp,
        exclusiveMoodLights,
        accountMoodLights,
      } = await upsertRemoteHeatProfiles(aggregated);

      if (accountMoodLights)
        dispatch(upsertAccountMoodLights(accountMoodLights));

      if (exclusiveMoodLights)
        dispatch(upsertExclusiveMoodLights(exclusiveMoodLights));

      dispatch(reduxUpsertAccountHeatProfiles(ahp));

      if (onDevice.length) {
        await peak?.cycles.writeHeatCycles(
          onDevice.map(hp => toHeatCycle(hp, getMoodLight)),
        );

        if (currentDevice)
          dispatch(
            updateDeviceHeatProfiles({
              id: currentDevice.id,
              heatProfiles: onDevice,
            }),
          );
      }
    },
    [
      deviceHeatProfiles,
      accountHeatProfiles,
      peak,
      currentDevice,
      upsertRemoteHeatProfiles,
      getMoodLight,
    ],
  );
};
